import { AxisBottom, AxisLeft } from '@visx/axis'
import { curveMonotoneX } from '@visx/curve'
import { localPoint } from '@visx/event'
import { LinearGradient } from '@visx/gradient'
import { GridRows } from '@visx/grid'
import { Group } from '@visx/group'
import { scaleLinear } from '@visx/scale'
import { AreaClosed, Bar, Line, LinePath } from '@visx/shape'
import { TooltipWithBounds, withTooltip } from '@visx/tooltip'
import { WithTooltipProvidedProps } from '@visx/tooltip/lib/enhancers/withTooltip'
import { extent } from 'd3-array'
import { MouseEvent, TouchEvent } from 'react'

import { CollateralAtRiskDataPoint } from '@/features/savings/data/savings-charts/collateral-at-risk-query/collateralAtRiskQuery'
import { ChartTooltipContent } from '@/ui/charts/ChartTooltipContent'
import { colors } from '@/ui/charts/colors'
import { defaultMargins, Margins } from '@/ui/charts/defaults'
import { formatUSDTicks, getVerticalDomainWithPadding } from '@/ui/charts/utils'

const PRIMARY_COLOR = '#F17DC0'

export interface CollateralAtRiskChartProps {
  height: number
  width: number
  data: CollateralAtRiskDataPoint[]
  margins?: Margins
}

function formatDropTick(value: { valueOf(): number }): string {
  return `${Math.round(value.valueOf() * 100)}%`
}

function CollateralAtRiskChartInner({
  height,
  width,
  margins = defaultMargins,
  data,
  showTooltip,
  hideTooltip,
  tooltipData,
  tooltipLeft = 0,
}: CollateralAtRiskChartProps & WithTooltipProvidedProps<CollateralAtRiskDataPoint>) {
  const innerWidth = width - margins.left - margins.right
  const innerHeight = height - margins.top - margins.bottom

  if (data.length === 0) {
    return null
  }

  const [minDrop, maxDrop] = extent(data, (d) => d.drop) as [number, number]
  const xValueScale = scaleLinear({
    range: [0, innerWidth],
    domain: [maxDrop, minDrop],
  })

  const [yMin, yMax] = getVerticalDomainWithPadding(0, Math.max(...data.map((d) => d.value)))
  const yValueScale = scaleLinear({
    range: [innerHeight, 0],
    domain: [yMin, yMax],
    nice: true,
  })

  function handleTooltip(event: TouchEvent<SVGRectElement> | MouseEvent<SVGRectElement>): void {
    const point = localPoint(event) || { x: 0 }
    const x = point.x - margins.left
    const domainX = xValueScale.invert(x)

    const closestElement = data.reduce((prev, curr) => {
      const prevDist = Math.abs(prev.drop - domainX)
      const currDist = Math.abs(curr.drop - domainX)
      return currDist < prevDist ? curr : prev
    })

    showTooltip({
      tooltipData: closestElement,
      tooltipLeft: xValueScale(closestElement.drop),
    })
  }

  return (
    <div>
      <svg width={width} height={height}>
        <Group left={margins.left} top={margins.top}>
          <GridRows
            scale={yValueScale}
            width={innerWidth}
            strokeDasharray="3"
            stroke={colors.backgroundLine}
            strokeWidth={1}
            pointerEvents="none"
            numTicks={5}
          />

          {/** biome-ignore lint/correctness/useUniqueElementIds: reason missing */}
          <LinearGradient
            id="collateral-area-gradient"
            from={PRIMARY_COLOR}
            to={PRIMARY_COLOR}
            fromOpacity={0.5}
            toOpacity={0}
          />

          <AreaClosed
            strokeWidth={2}
            data={data}
            x={(d) => xValueScale(d.drop)}
            y={(d) => yValueScale(d.value)}
            yScale={yValueScale}
            curve={curveMonotoneX}
            fill="url(#collateral-area-gradient)"
          />

          <LinePath
            stroke={PRIMARY_COLOR}
            strokeWidth={2}
            data={data}
            x={(d) => xValueScale(d.drop)}
            y={(d) => yValueScale(d.value)}
            curve={curveMonotoneX}
          />

          <AxisBottom
            top={innerHeight - margins.bottom / 4}
            scale={xValueScale}
            strokeWidth={0}
            hideAxisLine
            numTicks={5}
            tickFormat={formatDropTick}
            tickLabelProps={() => ({
              fill: colors.axisTickLabel,
              fontSize: 10,
              textAnchor: 'middle' as const,
              dy: 4,
            })}
          />

          <AxisLeft
            left={0}
            scale={yValueScale}
            strokeWidth={0}
            hideAxisLine
            numTicks={5}
            tickFormat={formatUSDTicks}
            tickLabelProps={() => ({
              fill: colors.axisTickLabel,
              fontSize: 10,
              dx: -margins.left + 10,
              dy: 3,
            })}
          />

          <Bar
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onTouchStart={handleTooltip}
            onTouchMove={handleTooltip}
            onMouseMove={handleTooltip}
            onMouseLeave={hideTooltip}
          />

          {tooltipData && (
            <g>
              <Line
                from={{ x: tooltipLeft, y: 0 }}
                to={{ x: tooltipLeft, y: innerHeight }}
                stroke={colors.tooltipLine}
                strokeWidth={2}
                pointerEvents="none"
                strokeDasharray="3"
              />
              <circle
                cx={tooltipLeft}
                cy={yValueScale(tooltipData.value)}
                r={8}
                fill={PRIMARY_COLOR}
                pointerEvents="none"
              />
              <circle
                cx={tooltipLeft}
                cy={yValueScale(tooltipData.value)}
                r={4}
                fill={colors.dot}
                stroke={colors.dotStroke}
                strokeWidth={3}
                pointerEvents="none"
              />
            </g>
          )}
        </Group>
      </svg>

      {tooltipData && (
        <TooltipWithBounds top={20} left={tooltipLeft + 40} unstyled applyPositionStyle className="pointer-events-none">
          <TooltipContent dataPoint={tooltipData} />
        </TooltipWithBounds>
      )}
    </div>
  )
}

function TooltipContent({ dataPoint }: { dataPoint: CollateralAtRiskDataPoint }) {
  return (
    <ChartTooltipContent>
      <div className="typography-label-4 text-secondary">{`SKY price drop: ${formatDropTick(dataPoint.drop)}`}</div>
      <div className="typography-label-3 flex items-center gap-1.5 text-primary-inverse">
        <div className="size-2 rounded-full" style={{ backgroundColor: PRIMARY_COLOR }} />
        <div>{`SKY CDP Vaults: ${formatUSDTicks(dataPoint.value)}`}</div>
      </div>
    </ChartTooltipContent>
  )
}

const CollateralAtRiskChartWithTooltip = withTooltip<CollateralAtRiskChartProps, CollateralAtRiskDataPoint>(
  CollateralAtRiskChartInner,
)

export { CollateralAtRiskChartWithTooltip as CollateralAtRiskChart }
